import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, GitCompare, Loader2, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { DataPagination } from "@/shared/components/DataPagination";
import { usePagination } from "@/shared/hooks/usePagination";
import { useToast } from "@/shared/hooks/use-toast";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { IntegrationBaseService } from "../services/integrationsBaseService";
import { useIntegrationBaseCache } from "../hooks/useIntegrationBaseCache";
import { instanceKind } from "../utils/integrationKind";
import {
  TOOL_STATES,
  countToolStates,
  displayMethod,
  normalizeToolStates,
  setToolState,
  toolState,
  toolStatesChanged,
  type ToolStates,
} from "../utils/toolStates";
import { removeApiTool, upsertApiTool, type ApiToolDraft } from "../utils/apiTools";
import { IntegrationToolDialog } from "./IntegrationToolDialog";
import { IntegrationToolsDiffDialog } from "./IntegrationToolsDiffDialog";
import type { IntegrationBase, IntegrationTool, ToolState } from "../types/integration";

interface IntegrationToolsTabProps {
  integration: IntegrationBase;
}


/** A partir de quantas tools a tabela pagina (regra do app: listas com 10+ itens). */
const PAGINATION_THRESHOLD = 10;

/**
 * Aba Tools (AB#87542): o estado de cada tool — ativa, pede confirmação, bloqueada — é
 * gravado junto, no botão Salvar.
 *
 * Definições: a organização edita (a instalação é a cópia dela do template), e cada mudança
 * grava na hora; "Comparar com o template" traz de volta o que o template tem de diferente.
 */
export const IntegrationToolsTab = ({ integration }: IntegrationToolsTabProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const applyUpdated = useIntegrationBaseCache();
  // Servidor MCP (AB#87477): a definição vem do servidor e é só leitura; o estado é da organização.
  const kind = instanceKind(integration);
  const tools = useMemo<IntegrationTool[]>(() => integration.mcp_config?.tools ?? [], [integration]);
  const savedStates = integration.mcp_config?.tool_states;

  const [draftStates, setDraftStates] = useState<ToolStates>(() => normalizeToolStates(savedStates, tools));
  const [isSavingStates, setIsSavingStates] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ tool: IntegrationTool | null } | null>(null);
  const [isSavingTool, setIsSavingTool] = useState(false);
  const [removing, setRemoving] = useState<IntegrationTool | null>(null);
  const [diffMode, setDiffMode] = useState<"template" | "refresh" | null>(null);

  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } = usePagination<IntegrationTool>({
    initialPageSize: PAGINATION_THRESHOLD,
  });

  // Instância nova (salvou, trouxe do template): o rascunho volta ao gravado.
  useEffect(() => {
    setDraftStates(normalizeToolStates(savedStates, tools));
  }, [savedStates, tools]);

  const dirty = toolStatesChanged(savedStates, draftStates, tools);
  const counts = countToolStates(draftStates, tools);
  const visibleTools = tools.length >= PAGINATION_THRESHOLD ? paginateData(tools) : tools;

  const errorToast = (error: unknown) =>
    toast({
      title: t("common.error"),
      description: resolveApiErrorMessage(error, t),
      variant: "destructive",
    });

  const handleSaveStates = async () => {
    setIsSavingStates(true);
    try {
      const updated = await IntegrationBaseService.updateTools(integration.id, {
        tool_states: normalizeToolStates(draftStates, tools),
      });
      await applyUpdated(updated, integration.id);
      toast({ title: t("integrations.messages.saved") });
    } catch (error) {
      errorToast(error);
    } finally {
      setIsSavingStates(false);
    }
  };

  /**
   * Grava só a lista de definições. Os estados em rascunho (ainda não salvos) ficam de fora:
   * mandá-los junto gravaria, sem pedir, uma mudança de estado que a pessoa não confirmou —
   * e o servidor já descarta o estado de uma tool removida.
   */
  const saveTools = async (next: IntegrationTool[]) => {
    const updated = await IntegrationBaseService.updateTools(integration.id, { tools: next });
    await applyUpdated(updated, integration.id);
  };

  const handleSaveTool = async (draft: ApiToolDraft) => {
    setIsSavingTool(true);
    try {
      await saveTools(upsertApiTool(tools, draft, editing?.tool?.name));
      setEditing(null);
      toast({ title: t("integrations.messages.saved") });
    } catch (error) {
      errorToast(error);
    } finally {
      setIsSavingTool(false);
    }
  };

  const handleRemoveTool = async () => {
    if (!removing) return;
    try {
      await saveTools(removeApiTool(tools, removing.name));
      toast({ title: t("integrations.tools.removed", { name: removing.name }) });
    } catch (error) {
      errorToast(error);
    } finally {
      setRemoving(null);
    }
  };

  const stateLabel = (state: ToolState) => t(`integrations.tools.states.${state}`);

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{t("integrations.tools.count", { count: tools.length })}</span>
            {tools.length > 0 &&
              TOOL_STATES.filter((state) => counts[state] > 0).map((state) => (
                <Badge key={state} variant={state === "blocked" ? "outline" : "secondary"} className="font-normal">
                  {stateLabel(state)}: {counts[state]}
                </Badge>
              ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {kind === "api" ? (
              <>
                <Button variant="outline" size="sm" className="gap-1" onClick={() => setDiffMode("template")}>
                  <GitCompare className="h-4 w-4" />
                  {t("integrations.tools.compareTemplate")}
                </Button>
                <Button variant="outline" size="sm" className="gap-1" onClick={() => setEditing({ tool: null })}>
                  <Plus className="h-4 w-4" />
                  {t("integrations.tools.add")}
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" className="gap-1" onClick={() => setDiffMode("refresh")}>
                <RefreshCw className="h-4 w-4" />
                {t("integrations.tools.refreshFromServer")}
              </Button>
            )}
            <Button size="sm" onClick={handleSaveStates} disabled={!dirty || isSavingStates}>
              {isSavingStates && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {t("integrations.actions.save")}
            </Button>
          </div>
        </div>

        {tools.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t("integrations.tools.empty")}</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>{t("integrations.tools.columns.name")}</TableHead>
                  <TableHead>{t("integrations.tools.columns.description")}</TableHead>
                  <TableHead className="w-20">{t("integrations.tools.columns.method")}</TableHead>
                  <TableHead className="w-48">{t("integrations.tools.columns.state")}</TableHead>
                  {kind === "api" && <TableHead className="w-20" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleTools.map((tool) => {
                  const isExpanded = expanded === tool.name;
                  return (
                    <Fragment key={tool.name}>
                      <TableRow>
                        <TableCell className="align-top">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => setExpanded(isExpanded ? null : tool.name)}
                            aria-label={t("integrations.tools.showSchema")}
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                        </TableCell>
                        <TableCell className="align-top">
                          <p className="font-mono text-xs font-medium">{tool.name}</p>
                          {tool.title && <p className="text-xs text-muted-foreground">{tool.title}</p>}
                        </TableCell>
                        <TableCell className="align-top">
                          <p className="line-clamp-2 text-xs text-muted-foreground">{tool.description || "—"}</p>
                        </TableCell>
                        <TableCell className="align-top">
                          <Badge variant="outline" className="font-mono text-[10px]">{displayMethod(tool)}</Badge>
                        </TableCell>
                        <TableCell className="align-top">
                          <Select
                            value={toolState(draftStates, tool.name)}
                            onValueChange={(value) =>
                              setDraftStates((atual) => setToolState(atual, tool.name, value as ToolState))
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TOOL_STATES.map((state) => (
                                <SelectItem key={state} value={state}>
                                  {stateLabel(state)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        {kind === "api" && (
                          <TableCell className="align-top">
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => setEditing({ tool })}
                                title={t("common.actions.edit")}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                onClick={() => setRemoving(tool)}
                                title={t("common.actions.remove")}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell />
                          <TableCell colSpan={kind === "api" ? 5 : 4}>
                            {tool.endpoint && (
                              <p className="mb-2 font-mono text-xs text-muted-foreground">{tool.endpoint}</p>
                            )}
                            {tool.description && <p className="mb-2 text-xs">{tool.description}</p>}
                            <pre className="max-h-72 overflow-auto rounded-md bg-muted p-3 text-[11px]">
                              {JSON.stringify(tool.inputSchema ?? {}, null, 2)}
                            </pre>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            {tools.length >= PAGINATION_THRESHOLD && (
              <DataPagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={tools.length}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
              />
            )}
          </>
        )}
      </CardContent>

      {editing && (
        <IntegrationToolDialog
          isOpen={true}
          tool={editing.tool}
          existing={tools}
          isSaving={isSavingTool}
          onCancel={() => setEditing(null)}
          onSave={handleSaveTool}
        />
      )}

      <AlertDialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("integrations.tools.removeTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("integrations.tools.removeConfirm", { name: removing?.name ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("integrations.actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveTool}>{t("common.actions.remove")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {diffMode && (
        <IntegrationToolsDiffDialog
          isOpen={true}
          mode={diffMode}
          integration={integration}
          onClose={() => setDiffMode(null)}
        />
      )}
    </Card>
  );
};
